import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { Box, Grid, Typography, Button } from "@mui/material";
import { TbBookmark } from "react-icons/tb";
import { AiOutlineRightCircle } from "react-icons/ai";
import {
  BASIC_INFORMATION_API,
  PERSONAL_INFORMATION_API,
  EDUCATION_INFORMATION_API,
  FAMILY_INFORMATION_API,
  FINANCIAL_INFORMATION_API,
  DEMANDS_INFORMATION_API,
  CANDIDATES_API,
  PROFILES_API,
} from "../api";
import { setPageTitle, updateSearchResults, addNewMessage } from "../slice";
import { useDate } from "../hooks";
import { PALETTE } from "../Theme/Palette";

const Field = ({ title, value }) => (
  <Grid xs={12} display="flex" flexDirection="row" marginY="0.4rem">
    <Grid item xl={3} lg={3} md={4} sm={5} xs={6} color={PALETTE.eightth} my="0.2rem">
      {title}
    </Grid>
    <Grid item xl={9} lg={9} md={8} sm={7} xs={6} color={PALETTE.eightth} my="0.2rem">
      {value}
    </Grid>
  </Grid>
);

const CandidateInformation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { simpleGregorianToPersian } = useDate();
  const [isCandidate, setIsCandidate] = useState(false);

  const isIranSelected = (countryId) => countryId === "1" || countryId === 1;

  const { data: basicInformation } = useQuery(["getDetailBasicInformation"], () =>
    BASIC_INFORMATION_API.GetDetailBasicInformationAfterApprovalInspector(id)
  );

  const { data: personalInformation } = useQuery(["getDetailPersonalInformation"], () =>
    PERSONAL_INFORMATION_API.GetDetailPersonalInformationAfterApprovalInspector(id)
  );

  const { data: educationInformation } = useQuery(["getEducationInformationDetails"], () =>
    EDUCATION_INFORMATION_API.GetEducationInformationDetailsAfterApprovalInspector(id)
  );

  const { data: familyInformation } = useQuery(["getFamilyInformationDetails"], () =>
    FAMILY_INFORMATION_API.GetFamilyInformationDetailsAfterApprovalInspector(id)
  );

  const { data: financialInformation } = useQuery(
    ["getFinancialCapabilityInformationDetails"],
    () =>
      FINANCIAL_INFORMATION_API.GetFinancialCapabilityInformationDetailsAfterApprovalInspector(id)
  );

  // const { data: demandsInformation } = useQuery(["getDemandsInformationDetails"], () =>
  //   DEMANDS_INFORMATION_API.GetDemandsInformationDetailsAfterApprovalInspector(id)
  // );

  useEffect(() => {
    dispatch(setPageTitle(""));
  }, [dispatch]);

  useEffect(() => {
    dispatch(updateSearchResults({ number: id, isCandidate }));
  }, [isCandidate, dispatch]);

  return (
    <Grid
      container
      position="relative"
      flexDirection="row"
      justifyContent="center"
      paddingTop="1rem"
    >
      <Grid
        item
        xl={12}
        lg={12}
        md={12}
        sm={12}
        xs={12}
        sx={{ boxSizing: "border-box", padding: "2rem 1rem 1rem 1rem" }}
      >
        <Typography variant="h1" sx={{ direction: "rtl" }}>
          اطلاعات کاربران
        </Typography>
      </Grid>
      <Box
        width={{ xs: "90", sm: "85", md: "65%", lg: "65", xl: "65%" }}
         sx={{
          boxSizing: "border-box",
          padding: "1rem 2rem",
          direction: "rtl",
        }}
      >
        <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h1" fontWeight="bold" fontSize="1.7rem">
            {id}
          </Typography>
        </Box>
        <Grid container display="flex" alignContent="center" justifyContent="center">
          <Grid
            item
            xs={12}
            display="flex"
            flexDirection="column"
            sx={{
              background: "#F6F0F0",
              borderRadius: "20px",
              padding: "0.3rem 1.7rem 1rem 0",
              marginTop: "4rem",
            }}
          >
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} marginY="1.5rem">
              <Typography variant="h1">اطلاعات هویتی</Typography>
            </Grid>
            <Field
              title="تاریخ تولد"
              value={simpleGregorianToPersian(
                basicInformation?.data?.data.birthDate.split("T").shift()
              ).replaceAll("-", "/")}
            />
            <Field
              title=" از خانواده سادات هستم"
              value={basicInformation?.data?.data.isSayed ? "بله" : "خیر"}
            />
            <Typography variant="h1" color={PALETTE.eightth} margin={"1rem 0 1rem 0"}>
              اطلاعات تماس و محل اقامت متقاضی
            </Typography>
            <Field
              title="خوابگاه زندگی میکنم"
              value={basicInformation?.data?.data.hasDorm ? "بله" : "خیر"}
            />
            <Field title="کشور" value={basicInformation?.data?.data.residenceCountryName} />
            {(!basicInformation?.data?.data?.residenceCountryId ||
              isIranSelected(basicInformation?.data?.data?.residenceCountryId)) && (
              <>
                <Field title="استان" value={basicInformation?.data?.data.residenceProvinceName} />
                <Field title="شهرستان" value={basicInformation?.data?.data.residenceCityName} />
                <Field title="منطقه" value={basicInformation?.data?.data.residenceRegionName} />
              </>
            )}
            {basicInformation?.data?.data?.residenceCountryId &&
              !isIranSelected(basicInformation?.data?.data?.residenceCountryId) && (
                <Field title="ایالت" value={basicInformation?.data?.data.residenceProvinceOther} />
              )}
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} margin="2rem 0 1rem 0">
              <Typography variant="h1" color={PALETTE.eightth}>
                اطلاعات تماس و محل اقامت خانواده
              </Typography>
            </Grid>
            <Field title="کشور" value={basicInformation?.data?.data.familyCountryName} />
            {(!basicInformation?.data?.data?.familyCountryId ||
              isIranSelected(basicInformation?.data?.data?.familyCountryId)) && (
              <>
                <Field title="استان" value={basicInformation?.data?.data.familyProvinceName} />
                <Field title="شهرستان" value={basicInformation?.data?.data.familyCityName} />
                <Field title="منطقه" value={basicInformation?.data?.data.familyRegionName} />
              </>
            )}
            {basicInformation?.data?.data?.familyCountryId &&
              !isIranSelected(basicInformation?.data?.data?.familyCountryId) && (
                <Field title="ایالت" value={basicInformation?.data?.data.familyProvinceOther} />
              )}
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            flexDirection="column"
            sx={{
              background: "#F6F0F0",
              borderRadius: "20px",
              padding: "0.3rem 1.7rem 1rem 0",
              marginTop: "0.6rem",
            }}
          >
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} marginY="1.5rem">
              <Typography variant="h1">اطلاعات اعتقادی</Typography>
            </Grid>
            <Field title="دین" value={personalInformation?.data?.data.religion} />
            <Field title="مذهب" value={personalInformation?.data?.data.religionType} />
            <Field title="تقید مذهبی" value={personalInformation?.data?.data.commitmentRate} />
            <Field
              title="گرایش سیاسی"
              value={personalInformation?.data?.data.politicalOrientation}
            />
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} margin="2rem 0 1rem 0">
              <Typography variant="h1" color={PALETTE.eightth}>
                اطلاعات ظاهری
              </Typography>
            </Grid>
            <Field title="قد" value={personalInformation?.data?.data.height} />
            <Field title="پوشش" value={personalInformation?.data?.data.clothingType} />
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} margin="2rem 0 1rem 0">
              <Typography variant="h1" color={PALETTE.eightth}>
                سابقه‌ی بیماری
              </Typography>
            </Grid>
            <Field
              title="معلولیت"
              value={
                personalInformation?.data?.data.disabledDescription
                  ? personalInformation?.data?.data.disabledDescription
                  : "---"
              }
            />
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} margin="2rem 0 1rem 0">
              <Typography variant="h1" color={PALETTE.eightth}>
                وضعیت تاهل
              </Typography>
            </Grid>
            <Field title="وضعیت تاهل" value={personalInformation?.data?.data.marriageHistory} />
            {personalInformation?.data?.data.marriageHistory !== "مجرد" && (
              <Field
                title=" فرزند دارد"
                value={personalInformation?.data?.data.childrenCount > 0 ? "بله" : "خیر"}
              />
            )}
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            flexDirection="column"
            sx={{
              background: "#F6F0F0",
              borderRadius: "20px",
              padding: "0.3rem 1.7rem 1rem 0",
              marginTop: "0.6rem",
            }}
          >
            {educationInformation?.data?.data.map((item, i) => (
              <>
                <Grid item xl={12} lg={12} md={12} sm={12} xs={12} marginY="1.5rem">
                  <Typography variant="h1">مدرک تحصیلی {NUMBER_DICTIONARY[i]}</Typography>
                </Grid>
                <Field title="نوع تحصیل" value={item.educationTypeName} />
                {item.educationType === 1 && (
                  <>
                    <Field title="وضعیت تحصیلی" value={item.educationStatus} />
                    <Field title="مقطع تحصیلی" value={item.grade} />
                    <Field title="شاخه تحصیلی" value={item.educationGroup} />
                    <Field title="نوع دانشگاه" value={item.universityType} />
                  </>
                )}
                {item.educationType === 2 && (
                  <>
                    <Field title="وضعیت تحصیلی" value={item.seminaryEducationStatus} />
                    <Field title="سطح تحصیلی" value={item.seminaryLevel} />
                    <Field title="وضعیت ملبس بودن" value={item.turbansStatus} />
                  </>
                )}
              </>
            ))}
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            flexDirection="column"
            sx={{
              background: "#F6F0F0",
              borderRadius: "20px",
              padding: "0.3rem 1.7rem 1rem 0",
              marginTop: "0.6rem",
            }}
          >
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} marginY="1.5rem">
              <Typography variant="h1">اطلاعات شغلی</Typography>
            </Grid>
            <Field title="وضعیت اشتغال" value={financialInformation?.data?.data.employmentStatus} />
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            flexDirection="column"
            sx={{
              background: "#F6F0F0",
              borderRadius: "20px",
              padding: "0.3rem 1.7rem 1rem 0",
              marginTop: "0.6rem",
            }}
          >
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} marginY="1.5rem">
              <Typography variant="h1">اطلاعات خانوادگی</Typography>
            </Grid>
            <Field
              title=" تعداد خواهر و برادر"
              value={
                familyInformation?.data?.data.brothersCount +
                familyInformation?.data?.data.sistersCount
              }
            />
          </Grid>
          {/* <Grid item xl={12} lg={12} md={12} sm={12} xs={12} marginY="1.5rem">
            <Typography variant="h1">اطلاعات خواسته‌ها</Typography>
          </Grid>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12} margin="0.5rem 0 0 0">
            <Typography variant="h1" color={PALETTE.eightth}>
              سطح تحصیلات
            </Typography>
          </Grid>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12} color={PALETTE.eightth}>
            <ul>
              {demandsInformation?.data?.data.educationGroupAllowedTitles.map((item) => (
                <li>{item}</li>
              ))}
            </ul>
          </Grid>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12} margin="1.5rem 0 0 0">
            <Typography variant="h1" color={PALETTE.eightth}>
              شاخه‌های تحصیلی
            </Typography>
          </Grid>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12} color={PALETTE.eightth}>
            <ul>
              {demandsInformation?.data?.data.educationLevelAllowedTitles.map((item) => (
                <li>{item}</li>
              ))}
            </ul>
          </Grid> */}
        </Grid>
      </Box>
    </Grid>
  );
};

export default CandidateInformation;

const NUMBER_DICTIONARY = {
  0: "یک",
  1: "دو",
  2: "سه",
  3: "چهار",
  4: "پنج",
  5: "شش",
  6: "هفت",
  7: "هشت",
  8: "نه",
  9: "ده",
};
